import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
  className?: string;
  autoFocus?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, className = '', autoFocus = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();
    
    const success = await login(data.email, data.password);
    
    if (success) {
      reset();
      onSuccess?.();
    }
  };

  const inputClass = (hasError: boolean) =>
    `w-full rounded-xl border px-4 py-3 text-base sm:text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary-muted)] focus:border-[color:var(--brand-primary)] ${
      hasError ? 'border-red-300 bg-red-50' : 'border-[color:var(--neutral-200)] bg-[color:var(--surface-primary)] hover:border-[color:var(--neutral-300)]'
    }`;

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--neutral-700)' }}>
            Email
          </label>
          <input
            {...register('email')}
            type="email"
            id="email"
            autoComplete="username"
            autoFocus={autoFocus}
            aria-invalid={!!errors.email}
            className={inputClass(!!errors.email)}
            style={{ color: 'var(--neutral-900)' }}
            placeholder="you@clinic.com"
          />
          {errors.email && <p className="mt-1.5 text-sm text-red-600">{errors.email.message}</p>}
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--neutral-700)' }}>
              Password
            </label>
            <Link to="/forgot-password" className="text-sm font-medium hover:underline" style={{ color: 'var(--brand-primary)' }}>
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              aria-invalid={!!errors.password}
              className={`${inputClass(!!errors.password)} pr-12`}
              style={{ color: 'var(--neutral-900)' }}
              placeholder="Your password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center px-4 transition-colors"
              style={{ color: 'var(--neutral-400)' }}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && <p className="mt-1.5 text-sm text-red-600">{errors.password.message}</p>}
        </div>

        {error && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition-[filter,opacity] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: 'var(--brand-primary)' }}
        >
          {isLoading ? (
            <>
              <Loader2 className="-ml-1 mr-2 h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>
    </div>
  );
};
